/**
 * Install prompt detection and iOS hint management
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let installed = false;

/** Fired whenever the browser becomes (or stops being) able to install the app, so any part of the page can follow it. */
export const INSTALL_STATE_EVENT = 'devstackkit:install-state';
const announce = () => window.dispatchEvent(new Event(INSTALL_STATE_EVENT));

/** True when the browser offered an install prompt that has not been used yet. */
export function canInstallNow(): boolean {
  return deferredPrompt !== null && !installed;
}

/**
 * Set up beforeinstallprompt listener
 */
export function initInstallPrompt(
  onPromptReady: (event: BeforeInstallPromptEvent) => void,
  onInstalled: () => void
): void {
  // Listen for beforeinstallprompt (Chrome, Edge, Samsung Internet)
  window.addEventListener('beforeinstallprompt', (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    onPromptReady(deferredPrompt);
    announce();
  });

  // Listen for app installed event
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    onInstalled();
    announce();
  });
}

/**
 * Trigger the install prompt
 */
export async function triggerInstallPrompt(): Promise<boolean> {
  if (!deferredPrompt) return false;

  try {
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      deferredPrompt = null;
      installed = true;
      announce();
      return true;
    }
  } catch (err) {
    console.error('[Install] Prompt failed:', err);
  }
  return false;
}

/**
 * Check if iOS Safari (for manual install hint)
 */
export function isIosSafari(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(ua) && /safari/.test(ua) && !/crios|fxios|chrome|firefox/.test(ua);
}

/**
 * Show/dismiss iOS install hint (stored in localStorage)
 */
export function getIosHintDismissed(): boolean {
  try {
    return localStorage.getItem('devstackkit:ios-hint-dismissed') === 'true';
  } catch {
    return false;
  }
}

export function dismissIosHint(): void {
  try {
    localStorage.setItem('devstackkit:ios-hint-dismissed', 'true');
  } catch {
    // Silently fail if localStorage unavailable
  }
}
